<template>
  <div>
    <overlay :value="loading"></overlay>
    <v-card class="pa-6">
      <v-card-title class="text-uppercase">
        <v-row class="my-1 justify-space-between align-center">
          <div>{{ this.service === null ? 'Agregar nuevo' : 'Detalle del' }} servicio</div>
        </v-row>
      </v-card-title>
      <hr class="my-3" />
      <v-card-text class="my-0 py-0">
        <v-form ref="newServiceForm" v-model="formValid">
          <v-container class="ma-0 pa-0">
            <!-- Fila 1: servicio + config básica -->
            <v-row class="ma-0 mt-2">
              <v-col cols="6">
                <v-select
                  label="Servicio"
                  v-model="newService.selectedService"
                  :items="services"
                  item-text="title"
                  item-value="id"
                  required
                  outlined
                  hide-details="auto"
                  dense
                  @change="showServiceDetails"
                ></v-select>
              </v-col>
              <v-col cols="3">
                <v-select
                  label="Moneda"
                  v-model="newService.maxAnnualAmountCurrency"
                  :items="currencies"
                  required
                  outlined
                  hide-details="auto"
                  dense
                ></v-select>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  label="Días de carencia"
                  v-model="newService.graceDays"
                  required
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  suffix="días"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Fila 2: montos -->
            <v-row class="ma-0 mt-2">
              <v-col cols="4">
                <amount-text-field
                  ref="maxAnnualAmountField"
                  label="Límite anual"
                  v-model="newService.maxAnnualAmount"
                  v-bind="amountSeparators"
                  :rules="maxAnnualAmountRules"
                  required
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  :prefix="newService.maxAnnualAmountCurrency"
                ></amount-text-field>
              </v-col>
              <v-col cols="4">
                <amount-text-field
                  label="Límite por evento"
                  v-model="newService.maxAmountPerEvent"
                  v-bind="amountSeparators"
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  :prefix="newService.maxAnnualAmountCurrency"
                ></amount-text-field>
              </v-col>
              <v-col cols="2">
                <amount-text-field
                  label="Deducible"
                  v-model="newService.serviceDeductible"
                  v-bind="amountSeparators"
                  :rules="deductibleRules"
                  outlined
                  dense
                  hide-details="auto"
                  :prefix="isPercentageDeductible ? '' : newService.serviceDeductibleCurrency"
                  :suffix="isPercentageDeductible ? '%' : ''"
                ></amount-text-field>
              </v-col>
              <v-col cols="2">
                <v-select
                  label="Unidad deducible"
                  v-model="newService.serviceDeductibleCurrency"
                  :items="deductibleUnits"
                  outlined
                  dense
                  hide-details="auto"
                ></v-select>
              </v-col>
            </v-row>

            <!-- Fila 3: conteo y tiempos -->
            <v-row class="ma-0 mt-2">
              <v-col cols="4">
                <v-text-field
                  label="Máx. eventos al año"
                  v-model="newService.maxAnnualEvents"
                  required
                  outlined
                  clearable
                  min="1"
                  dense
                  type="number"
                  hide-details="auto"
                  suffix="eventos"
                ></v-text-field>
              </v-col>
              <v-col cols="4">
                <v-text-field
                  label="Tiempo de revisión"
                  v-model="newService.refundReviewDays"
                  required
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  suffix="días"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Fila 3b: contrato TPA que factura este servicio -->
            <v-row class="ma-0 mt-2">
              <v-col cols="6">
                <v-select
                  label="Contrato TPA que factura este servicio"
                  v-model="newService.contratoId"
                  :items="contratoItems"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  hint="Solo contratos TPA. Vacío = este servicio no factura por unidad."
                  persistent-hint
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-select
                  label="Redirección de reembolso"
                  v-model="newService.refundRedirectProfileId"
                  :items="refundRedirectProfiles"
                  item-text="counterpartName"
                  item-value="id"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  hint="El miembro verá los contactos de la contraparte en vez del formulario de reembolso"
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>

            <!-- Fila 4: límites de edad para elegibilidad de reembolso -->
            <v-row class="ma-0 mt-2">
              <v-col cols="6">
                <v-text-field
                  label="Límite inferior de edad"
                  v-model.number="newService.lowerLimit"
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  suffix="meses"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Límite superior de edad"
                  v-model.number="newService.upperLimit"
                  outlined
                  dense
                  min="0"
                  type="number"
                  hide-details="auto"
                  suffix="meses"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="ma-0">
              <v-col cols="12">
                <v-textarea
                  label="De qué trata el servicio"
                  v-model="newService.whatIs"
                  required
                  outlined
                  auto-grow
                  rows="3"
                  dense
                  hide-details="auto"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <hr class="my-3" />
      <v-card-actions class="justify-end">
        <v-btn text color="primary" rounded @click="closeDialog">Cerrar</v-btn>
        <v-btn
          :disabled="!formValid"
          color="primary"
          rounded
          @click="validate"
        >
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="showSnackbar" :timeout="5000">
      {{ snackbarContent }}

      <template v-slot:action="{ attrs }">
        <v-btn color="red" text v-bind="attrs" @click="showSnackbar = false"> Entendido </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Overlay from '../components/Overlay.vue';
import AmountTextField from '../components/AmountTextField.vue';
import PlansService from '../services/plans.service';
import ServicesService from '../services/services.service';
import CurrencyUtils from '../utils/currency.utils';
import ContratoService from '../services/contrato.service';
import RefundRedirectProfilesService from '../services/refund-redirect-profiles.service';

/**
 * Códigos (`producto.code`, NO `producto.nombre`) de los productos que pueden
 * facturar un service-plan por unidad. El nombre se puede renombrar libremente (ej. "TPA"
 * -> "TPA de claims") sin que este filtro se rompa.
 */
const SERVICE_PLAN_CONTRATO_PRODUCTS = ['TPA'];

/** Valor de `serviceDeductibleCurrency` que convierte el deducible en un porcentaje. */
const PERCENTAGE_UNIT = '%';

/** Código con que el backend rechaza un límite anual menor a eventos × límite por evento. */
const ANNUAL_AMOUNT_BELOW_EVENT_CAPACITY = 'ANNUAL_AMOUNT_BELOW_EVENT_CAPACITY';

const GENERIC_SAVE_ERROR = 'Ocurrió un error guardando el servicio, intenta más tarde';

export default {
  components: { Overlay, AmountTextField },
  name: 'ServicePlanMaintainer',
  props: ['service', 'plan'],
  data() {
    return {
      loading: false,
      showSnackbar: false,
      snackbarContent: GENERIC_SAVE_ERROR,
      newService: {},

      services: [],
      // Contratos TPA elegibles para facturar este service-plan.
      contratoOptions: [],
      // Perfiles de redirección de reembolso (125-service-refund-redirect).
      refundRedirectProfiles: [],
      formValid: false
    };
  },
  mounted() {
    this.fetchServices();
    this.fetchContratoOptions();
    this.fetchRefundRedirectProfiles();
    this.parseService(this.service !== null);
    this.$refs.newServiceForm.resetValidation();
  },
  computed: {
    currencies() {
      if (this.plan && this.plan.country === 'Perú') {
        return ['UF', 'S/'];
      }
      // Spec 084: cupos de reembolso en pesos mexicanos, o en dólares como el
      // precio del plan de Crehana MX. La UF no aplica en México.
      if (CurrencyUtils.countryIso(this.plan && this.plan.country) === 'MX') {
        return ['MX$', 'US$'];
      }
      return ['UF', '$'];
    },
    // El deducible puede pactarse como monto fijo (en cualquiera de las monedas
    // del plan) o como un porcentaje del evento.
    deductibleUnits() {
      return [...this.currencies, PERCENTAGE_UNIT];
    },
    isPercentageDeductible() {
      return this.newService.serviceDeductibleCurrency === PERCENTAGE_UNIT;
    },
    deductibleRules() {
      if (!this.isPercentageDeductible) return [];
      const deductible = Number(this.newService.serviceDeductible);
      return [
        () =>
          (deductible >= 0 && deductible <= 100) ||
          'Un deducible en % debe estar entre 0 y 100'
      ];
    },
    /**
     * Mínimo que exige el backend para el límite anual: límite por evento × máx. eventos.
     * Solo aplica cuando los tres topes están configurados (igual que el invariante del
     * backend); si falta alguno no hay mínimo.
     */
    annualAmountRequiredMinimum() {
      const { maxAnnualAmount, maxAmountPerEvent, maxAnnualEvents } = this.newService;
      if (!Number(maxAnnualAmount) || !Number(maxAmountPerEvent) || !Number(maxAnnualEvents)) {
        return null;
      }
      return Number(maxAmountPerEvent) * Number(maxAnnualEvents);
    },
    maxAnnualAmountRules() {
      const min = this.annualAmountRequiredMinimum;
      const annual = Number(this.newService.maxAnnualAmount);
      return [() => min === null || annual >= min || this.annualAmountBelowMessage(min)];
    },
    // Chile separa miles con punto y decimales (UF) con coma; Perú y México al revés.
    amountSeparators() {
      return CurrencyUtils.countryMoney(this.plan && this.plan.country).locale === 'es-CL'
        ? { thousandsSeparator: '.', decimalSeparator: ',' }
        : { thousandsSeparator: ',', decimalSeparator: '.' };
    },
    /**
     * Contratos TPA, únicos que un service-plan puede facturar.
     *
     * Se filtra solo por `producto.code`, que es lo mismo que valida el backend: exigir
     * además una línea concreta dejaría la lista vacía la próxima vez que el producto
     * cambie de línea.
     */
    contratoItems() {
      return this.contratoOptions
        .filter(c => SERVICE_PLAN_CONTRATO_PRODUCTS.includes(c.producto?.code))
        .map(c => ({
          text: `${c.codigo} — ${c.company?.name || '—'} (${c.producto?.nombre})`,
          value: c.id
        }));
    }
  },
  watch: {
    service(val) {
      this.newService = {};
      this.$refs.newServiceForm.resetValidation();
      this.parseService(val !== null);
    },
    // Vuetify 2 compara las rules con deepEqual, que trata dos closures distintas como
    // iguales: si cambia el límite por evento o los eventos, el campo anual no se
    // revalida solo y su error (y el botón Guardar deshabilitado) queda pegado.
    annualAmountRequiredMinimum() {
      this.$nextTick(() => this.$refs.maxAnnualAmountField?.validate?.());
    },
    'plan.countryId'(val, oldVal) {
      if (val !== oldVal) this.fetchServices();
    },
    'plan.servicePlanDetails'(val) {
      if (this.service === null) {
        this.newService.order = val?.length
          ? Math.max(...val.map(s => s.order)) + 1
          : 0;
      }
    },
    'newService.maxAnnualAmountCurrency'(val) {
      this.newService.maxAmountPerEventCurrency = val;
      // El deducible sigue la moneda del plan salvo que se haya elegido
      // explícitamente expresarlo en porcentaje.
      if (this.newService.serviceDeductibleCurrency !== PERCENTAGE_UNIT) {
        this.$set(this.newService, 'serviceDeductibleCurrency', val);
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('close-dialog');
    },
    parseService(exist) {
      if (exist) {
        this.newService = {
          id: this.service.id,
          planId: this.plan.id,
          serviceId: this.service.service?.id,
          selectedService: this.service.service?.id,
          graceDays: this.service.graceDays || 0,
          maxAmountPerEventCurrency: this.service.maxAmountPerEventCurrency,
          maxAnnualAmount: this.service.maxAnnualAmount || 0,
          maxAnnualEvents: this.service.maxAnnualEvents || 0,
          whatIs: this.service.whatIs,
          order: this.service.order,
          maxAnnualAmountCurrency: this.service.maxAnnualAmountCurrency,
          maxAmountPerEvent: this.service.maxAmountPerEvent,
          refundReviewDays: this.service.refundReviewDays || 10,
          serviceDeductible: this.service.serviceDeductible || 0,
          serviceDeductibleCurrency:
            this.service.serviceDeductibleCurrency ||
            this.service.maxAnnualAmountCurrency,
          lowerLimit: this.service.lowerLimit ?? 0,
          upperLimit: this.service.upperLimit ?? 300,
          contratoId: this.service.contratoId || null,
          refundRedirectProfileId: this.service.refundRedirectProfileId || null
        };
      } else {
        this.newService = {
          maxAnnualAmount: 0,
          maxAnnualAmountCurrency: '',
          maxAnnualEvents: 0,
          maxAmountPerEvent: 0,
          maxAmountPerEventCurrency: '',
          order: this.plan.servicePlanDetails?.length
            ? Math.max(...this.plan.servicePlanDetails.map(s => s.order)) + 1
            : 0,
          planId: this.plan.id,
          serviceId: '',
          graceDays: 0,
          whatIs: '',
          refundReviewDays: 10,
          serviceDeductible: 0,
          serviceDeductibleCurrency: '',
          lowerLimit: 0,
          upperLimit: 300,
          contratoId: null,
          refundRedirectProfileId: null
        };
      }
    },
    async fetchContratoOptions() {
      const res = await ContratoService.getAll();
      if (res.status === 'ok') {
        this.contratoOptions = res.data || [];
      }
    },
    async fetchRefundRedirectProfiles() {
      const res = await RefundRedirectProfilesService.getAll();
      if (res.status === 'ok') {
        this.refundRedirectProfiles = res.data || [];
      }
    },
    async fetchServices() {
      // Los montos por defecto dependen del país del plan. Sin país no se cae a
      // ningún país "por defecto" (antes terminaba proponiendo los de Chile).
      const countryId = this.plan && this.plan.countryId;
      if (!countryId) {
        this.services = [];
        this.showSnackbar = true;
        this.snackbarContent = 'El plan no tiene país asignado; no se pueden cargar los servicios';
        return;
      }
      this.loading = true;
      try {
        const response = await ServicesService.getAllServices(countryId, true);
        if (response.status === 'ok') {
          this.services = [...response.data].sort((a, b) =>
            a.title.localeCompare(b.title, 'es', { sensitivity: 'base' })
          );
        }
      } catch {
        this.showSnackbar = true;
        this.snackbarContent = 'No se pudieron cargar los servicios, intenta más tarde';
      } finally {
        this.loading = false;
      }
    },
    showServiceDetails(serviceId) {
      const selectedService = this.services.find(service => service.id === serviceId);
      // Bugfix: `serviceId` debe quedar seteado siempre que se elige un servicio,
      // no solo cuando trae `servicePlanDefault`. Antes, un servicio sin defaults
      // (ej. "Esterilización") dejaba `serviceId` vacío y el guardado fallaba con
      // un 400 silencioso ("serviceId must be a valid UUID") que el snackbar
      // genérico no dejaba diagnosticar.
      this.newService.serviceId = serviceId;
      if (!selectedService || this.service !== null) return;
      // La descripción es del servicio (igual en todos los países); el resto
      // viene del default del país del plan.
      const countryDefault = selectedService.servicePlanDefault || {};
      this.newService = {
        ...this.newService,
        maxAnnualAmount: countryDefault.maxAnnualAmount ?? 0,
        maxAmountPerEvent: countryDefault.maxAmountPerEvent ?? 0,
        maxAnnualEvents: countryDefault.maxAnnualEvents ?? 0,
        graceDays: countryDefault.graceDays ?? 0,
        refundReviewDays: countryDefault.refundReviewDays ?? 10,
        whatIs: selectedService.whatIs || ''
      };
    },
    validate() {
      if (this.$refs.newServiceForm.validate()) {
        // null explícito (no undefined, que JSON omite): en el PUT, omitir el campo conserva el perfil anterior.
        this.newService.refundRedirectProfileId = this.newService.refundRedirectProfileId || null;
        if (this.service === null) {
          this.createServicePlan();
        } else {
          this.updateServicePlan();
        }
      }
    },
    async createServicePlan() {
      this.loading = true;
      this.newService.planId = this.plan.id;
      const res = await PlansService.addServiceToPlan(this.newService);
      this.loading = false;
      this.handleSaveResult(res);
    },
    async updateServicePlan() {
      this.loading = true;
      const res = await PlansService.updateServicePlan(this.newService);
      this.loading = false;
      this.handleSaveResult(res);
    },
    handleSaveResult(res) {
      if (res.status === 'ok') {
        this.$refs.newServiceForm.resetValidation();
        this.closeDialog();
        return;
      }
      this.showSnackbar = true;
      this.snackbarContent =
        res.code === ANNUAL_AMOUNT_BELOW_EVENT_CAPACITY
          ? this.annualAmountBelowMessage(res.requiredMinimum)
          : GENERIC_SAVE_ERROR;
    },
    annualAmountBelowMessage(min) {
      const amount = Number(min).toLocaleString('es-CL');
      const currency = this.newService.maxAnnualAmountCurrency || '';
      return `El límite anual debe ser al menos ${currency} ${amount} (límite por evento × máx. eventos al año)`;
    },
  }
};
</script>
